/**
 * Shape of Amplify Storage v6 transfer callbacks (`transferredBytes` / `totalBytes`)
 * and legacy v5-style xhr progress (`loaded` / `total`).
 */
export type StorageTransferProgressInput = {
  transferredBytes?: number;
  totalBytes?: number;
  loaded?: number;
  total?: number;
};

function percentFromDownloadProgressString(value: string): number {
  const match = value.trim().match(/^(\d+(?:\.\d+)?)\s*%$/);
  if (!match) {
    return 0;
  }
  const n = parseFloat(match[1]);
  if (!Number.isFinite(n)) {
    return 0;
  }
  return Math.min(100, Math.max(0, n));
}

/**
 * Maps storage transfer progress to a 0–100 UI value.
 *
 * Accepts Amplify v6 `onProgress` objects, legacy `loaded` / `total`, or the
 * `"12%"` strings produced by `downloadFile` (`@utils/storage`).
 */
export function storageTransferProgressPercent(
  progress: StorageTransferProgressInput | string | null | undefined
): number {
  if (progress == null) {
    return 0;
  }

  if (typeof progress === "string") {
    return percentFromDownloadProgressString(progress);
  }

  const transferred =
    typeof progress.transferredBytes === "number"
      ? progress.transferredBytes
      : typeof progress.loaded === "number"
        ? progress.loaded
        : 0;

  const total =
    typeof progress.totalBytes === "number" && progress.totalBytes > 0
      ? progress.totalBytes
      : typeof progress.total === "number" && progress.total > 0
        ? progress.total
        : 0;

  if (!total) {
    return 0;
  }

  const ratio = transferred / total;
  if (!Number.isFinite(ratio)) {
    return 0;
  }

  return Math.min(100, Math.max(0, ratio * 100));
}
